import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from '../../axiosConfig';
import { fetchMyStore } from '../store/storeSlice';
import './LiveToggle.css';

/**
 * Storefront live/offline pill in the topbar.
 *
 * Reads `store.isLive` from Redux (single source of truth — the same field
 * other surfaces use) and re-fetches the store after toggling so any other
 * subscribers stay in sync. Disables itself while the request is in
 * flight to prevent double-fires.
 */
const LiveToggle = () => {
  const dispatch = useDispatch();
  const store = useSelector((s) => s.store?.current);
  const isLive = store?.isLive ?? true;
  const [busy, setBusy] = useState(false);

  const onToggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post('/merchants/store/toggle-live');
      if (data?.success) {
        // Refresh from Redux so every consumer (Overview store-live pill,
        // Dashboard, etc.) reflects the new state without a window reload.
        dispatch(fetchMyStore());
      }
    } catch (err) {
      console.error('[LiveToggle] toggle failed:', err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      className={`live-toggle ${isLive ? 'is-live' : 'is-offline'} ${busy ? 'busy' : ''}`}
      onClick={onToggle}
      disabled={busy}
      aria-pressed={isLive}
      aria-label={isLive ? 'Storefront is live — click to take offline' : 'Storefront is offline — click to take live'}
    >
      <span className="live-toggle-dot" aria-hidden="true" />
      <span className="live-toggle-label">{isLive ? 'Live' : 'Offline'}</span>
      <span className="live-toggle-switch" aria-hidden="true">
        <span className="live-toggle-knob" />
      </span>
    </button>
  );
};

export default LiveToggle;
